/**
 * This tab, lent to the learner's own AI agent (docs/agents.md, worker/agent-bridge.ts).
 *
 * The learner presses "Let my agent use this tab"; this opens a socket to the
 * Worker, and each MCP tool call the agent makes arrives here as one request
 * and goes back as one answer. One module for the whole page, so the socket
 * survives moving between levels: the App registers the course (the level
 * list, and opening a level), and the play screen registers the level that is
 * open while it is mounted.
 *
 * Requests are answered one at a time, in order, so an agent's commands
 * never interleave. Everything done is put in a short log the strip shows,
 * so the learner sees each thing the agent did in their name.
 *
 * Nothing here decides what an agent may do: it can do what the learner's
 * buttons do (the same calls Iris uses, the editor's save, Check my work), on
 * levels the learner can open, and nothing else.
 */
import type { AgentDid } from './strings/agents';

/** What the play screen hands over for the level it has open. */
export interface LevelDriver {
  missionId: string;
  /** False while the level is still opening. */
  ready(): boolean;
  status(): unknown;
  run(command: string): Promise<{ code: number; stdout: string; stderr: string }>;
  listFiles(path: string): Promise<unknown>;
  readFile(path: string): Promise<{ path: string; content: string }>;
  writeFile(path: string, content: string): Promise<unknown>;
  check(): Promise<string>;
}

/** What the App hands over: every level, and a way to open one. */
export interface CourseDriver {
  levels(): unknown;
  /** Null when `id` can be opened; otherwise why not. */
  refuseOpen(id: string): string | null;
  open(id: string): void;
}

export type BridgeState = 'off' | 'connecting' | 'on' | 'taken-over' | 'lost';
export interface BridgeEntry {
  at: number;
  what: AgentDid;
  detail: string;
  failed: boolean;
}
export interface BridgeSnapshot {
  state: BridgeState;
  log: readonly BridgeEntry[];
}

const STORE = 'engineeros.agentBridge';
const LOG_SIZE = 12;
const PING_MS = 30_000;
/** Output kept per stream: an agent rarely needs more, and the socket has a limit. */
const MAX_OUTPUT = 100_000;
/**
 * Longest an answer waits. Agents' MCP clients give up on one call after
 * about a minute (measured 20 October 2026: a checkpoint's server grade, queued,
 * outlasted Claude Code's wait three times), so a slow open or check answers
 * "still going" inside this and the result is read with `status`.
 */
const ANSWER_WITHIN_MS = 50_000;
const TAKEN_OVER = 4001;

let socket: WebSocket | null = null;
let ping: ReturnType<typeof setInterval> | null = null;
let retry: ReturnType<typeof setTimeout> | null = null;
let retries = 0;
let snapshot: BridgeSnapshot = { state: 'off', log: [] };
let level: LevelDriver | null = null;
let course: CourseDriver | null = null;
let queue: Promise<void> = Promise.resolve();
/** A check still running, so a second check_work waits on it rather than spending another graded check. */
let checking: { missionId: string; done: Promise<string> } | null = null;
const listeners = new Set<() => void>();

function set(next: Partial<BridgeSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  for (const cb of [...listeners]) cb();
}

export function subscribeBridge(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
export const bridgeSnapshot = (): BridgeSnapshot => snapshot;

/** The play screen's level, while it is mounted. Returns the unregister. */
export function registerLevel(driver: LevelDriver): () => void {
  level = driver;
  tell({ report: 'level', level: driver.missionId });
  return () => {
    if (level === driver) level = null;
  };
}
export function registerCourse(driver: CourseDriver): () => void {
  course = driver;
  return () => {
    if (course === driver) course = null;
  };
}

export function connectBridge(): void {
  remember(true);
  if (socket && (socket.readyState === WebSocket.OPEN || socket.readyState === WebSocket.CONNECTING)) return;
  if (retry) clearTimeout(retry);
  retry = null;
  set({ state: snapshot.state === 'lost' ? 'lost' : 'connecting' });
  const url = new URL('/api/agent/bridge/ws', window.location.href);
  url.protocol = url.protocol === 'https:' ? 'wss:' : 'ws:';
  const ws = new WebSocket(url.href);
  socket = ws;
  ws.addEventListener('open', () => {
    retries = 0;
    set({ state: 'on' });
    if (level) tell({ report: 'level', level: level.missionId });
    ping = setInterval(() => ws.readyState === WebSocket.OPEN && ws.send('ping'), PING_MS);
  });
  ws.addEventListener('message', (event) => {
    if (typeof event.data !== 'string' || event.data === 'pong') return;
    let ask: { id?: unknown; op?: unknown; args?: unknown };
    try {
      ask = JSON.parse(event.data);
    } catch {
      return;
    }
    if (typeof ask.id !== 'string' || typeof ask.op !== 'string') return;
    const id = ask.id;
    const op = ask.op;
    const args = ask.args && typeof ask.args === 'object' ? (ask.args as Record<string, unknown>) : {};
    queue = queue.then(() => answer(ws, id, op, args));
  });
  ws.addEventListener('close', (event) => {
    if (ping) clearInterval(ping);
    ping = null;
    if (socket !== ws) return;
    socket = null;
    if (event.code === TAKEN_OVER) {
      remember(false);
      set({ state: 'taken-over' });
      return;
    }
    if (snapshot.state === 'off') return;
    // Dropped (a deploy, a network change, a sleeping laptop): try again, more slowly each time.
    set({ state: 'lost' });
    retries += 1;
    retry = setTimeout(connectBridge, Math.min(30_000, 1000 * 2 ** Math.min(retries, 5)));
  });
}

export function disconnectBridge(): void {
  remember(false);
  if (retry) clearTimeout(retry);
  retry = null;
  set({ state: 'off' });
  const ws = socket;
  socket = null;
  ws?.close(1000, 'stopped');
}

/** Reconnect after a reload if this tab was lent before it. */
export function resumeBridge(): void {
  try {
    if (sessionStorage.getItem(STORE) === 'on') connectBridge();
  } catch {
    /* storage unavailable: the learner presses the button again */
  }
}

function remember(on: boolean): void {
  try {
    if (on) sessionStorage.setItem(STORE, 'on');
    else sessionStorage.removeItem(STORE);
  } catch {
    /* storage unavailable */
  }
}

/**
 * Tell the bridge something unasked, while this tab is lent: the level it
 * opened, or a check the learner pressed. The server keeps it in the record
 * of agent runs (worker/agent-runs.ts), the same facts the rail shows. Not
 * lent, nothing is sent.
 */
function tell(body: Record<string, unknown>): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(body));
}

/** A check the learner pressed on a level, for the record of agent runs while the tab is lent. */
export function reportCheck(missionId: string, passed: boolean, summary: string): void {
  tell({ report: 'check', level: missionId, passed, summary: summary.slice(0, 200) });
}

function log(what: AgentDid, detail: string, failed: boolean): void {
  set({ log: [{ at: Date.now(), what, detail: detail.length > 120 ? `${detail.slice(0, 117)}…` : detail, failed }, ...snapshot.log].slice(0, LOG_SIZE) });
}

class Refusal extends Error {
  constructor(readonly code: string, detail: string) {
    super(detail);
  }
}

async function answer(ws: WebSocket, id: string, op: string, args: Record<string, unknown>): Promise<void> {
  const reply = (body: Record<string, unknown>) => {
    // The level it had open, for the record of agent runs (worker/agent-bridge.ts).
    if (ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ id, ...body, level: level?.missionId ?? null }));
  };
  const what = op as AgentDid;
  const named = typeof args.command === 'string' ? args.command : typeof args.path === 'string' ? args.path : typeof args.id === 'string' ? args.id : '';
  const detail = op === 'list_files' && named === '' ? '.' : named;
  try {
    const result = await perform(op, args);
    if (op !== 'status' && op !== 'levels') log(what, detail, false);
    reply({ ok: true, result });
  } catch (err) {
    log(what, detail, true);
    const code = err instanceof Refusal ? err.code : 'failed';
    reply({ ok: false, error: code, detail: err instanceof Error ? err.message : String(err) });
  }
}

function openLevel(): LevelDriver {
  if (!level) throw new Refusal('no-level', 'No level is open in the tab. Use list_levels and open_level.');
  if (!level.ready()) throw new Refusal('opening', 'The level is still opening. Try again in a few seconds.');
  return level;
}

const text = (v: unknown): string => (typeof v === 'string' ? v : '');
const cut = (s: string) => (s.length > MAX_OUTPUT ? `${s.slice(0, MAX_OUTPUT)}\n[cut: ${s.length - MAX_OUTPUT} more characters]` : s);

async function perform(op: string, args: Record<string, unknown>): Promise<unknown> {
  switch (op) {
    case 'status':
      if (!level) return { screen: 'course', level: null, hint: 'No level is open. Use list_levels and open_level.' };
      return { screen: 'level', ready: level.ready(), checking: checking?.missionId === level.missionId, ...(level.status() as object) };
    case 'levels':
      if (!course) throw new Refusal('not-ready', 'The course is still loading.');
      return course.levels();
    case 'open': {
      if (!course) throw new Refusal('not-ready', 'The course is still loading.');
      const target = text(args.id);
      const why = course.refuseOpen(target);
      if (why) throw new Refusal('cannot-open', why);
      if (level?.missionId !== target) course.open(target);
      const until = Date.now() + ANSWER_WITHIN_MS;
      while (Date.now() < until) {
        if (level?.missionId === target && level.ready()) return { screen: 'level', ready: true, ...(level.status() as object) };
        await new Promise((resolve) => setTimeout(resolve, 250));
      }
      return { screen: 'level', ready: false, opening: target, message: 'Still opening. Call course_status in a few seconds.' };
    }
    case 'run': {
      const command = text(args.command);
      if (!command.trim()) throw new Refusal('bad-request', 'command is empty');
      const result = await openLevel().run(command);
      return { code: result.code, stdout: cut(result.stdout), stderr: cut(result.stderr) };
    }
    case 'list_files':
      return openLevel().listFiles(text(args.path));
    case 'read_file':
      return openLevel().readFile(text(args.path));
    case 'write_file':
      return openLevel().writeFile(text(args.path), text(args.content));
    case 'check': {
      const driver = openLevel();
      if (checking?.missionId !== driver.missionId) {
        const done = driver.check();
        const entry = { missionId: driver.missionId, done };
        checking = entry;
        void done.then(
          () => { if (checking === entry) checking = null; },
          () => { if (checking === entry) checking = null; },
        );
      }
      const summary = await Promise.race([checking!.done, new Promise<null>((resolve) => setTimeout(() => resolve(null), ANSWER_WITHIN_MS))]);
      if (summary === null) {
        return {
          checking: true,
          message: 'Still checking: a checkpoint is graded on our server and can wait in a queue. Call course_status in a minute; its lastCheck is the result. Do not press check again meanwhile.',
        };
      }
      return { summary };
    }
    default:
      throw new Refusal('unknown-op', op);
  }
}
